import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Search, Users, BarChart3, Activity, Calendar, KeyRound, X } from "lucide-react";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeUser, setActiveUser] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  useEffect(() => {
    fetchAll(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // initial

  const fetchAll = async (search) => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        api.get("/admin/users", { params: search ? { q: search } : {} }),
        api.get("/admin/metrics"),
      ]);
      setUsers(u.data.users);
      setMetrics(m.data);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchAll(q);
  };

  const openRecovery = async (user) => {
    setActiveUser(user);
    setRecovery(null);
    setRecoveryLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/recovery`);
      setRecovery(data);
    } catch (e) {
      setRecovery({ tokens: [], error: formatApiError(e.response?.data?.detail) });
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-page">
      <header>
        <p className="tk-label">Administración</p>
        <h1 className="font-outfit text-3xl text-ink-900 mt-1">Panel de control</h1>
      </header>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Usuarios" value={metrics?.total_users ?? "—"} testId="admin-metric-users" />
        <MetricCard icon={BarChart3} label="Registros" value={metrics?.total_logs ?? "—"} testId="admin-metric-logs" />
        <MetricCard icon={Activity} label="Completados" value={metrics?.completed_logs ?? "—"} testId="admin-metric-completed" />
        <MetricCard icon={Calendar} label="Activos hoy" value={metrics?.active_today ?? "—"} testId="admin-metric-today" />
      </div>

      {/* Search */}
      <form onSubmit={onSearch} className="tk-card" data-testid="admin-search-form">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o email…"
            className="tk-input pl-11"
            data-testid="admin-search-input"
          />
        </div>
      </form>

      {/* Users table */}
      <div className="tk-card overflow-hidden p-0" data-testid="admin-users-card">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead className="bg-sand-50 border-b border-sand-200/70 text-left">
              <tr className="font-dmsans text-[11px] uppercase tracking-wider text-ink-600">
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3 hidden md:table-cell">Rol</th>
                <th className="px-4 py-3 hidden sm:table-cell">Alta</th>
                <th className="px-4 py-3 hidden md:table-cell">Último acceso</th>
                <th className="px-4 py-3">Registros</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-600">Cargando…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-600" data-testid="admin-no-users">Sin resultados.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-b border-sand-200/40 hover:bg-sand-50/60 transition" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-outfit text-sm text-ink-900">{u.name}</p>
                    <p className="font-dmsans text-xs text-ink-600">{u.email}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-dmsans ${u.role === "admin" ? "bg-sage-100 text-sage-700" : "bg-sand-100 text-ink-600"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell font-dmsans text-xs text-ink-600">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("es-AR") : "—"}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell font-dmsans text-xs text-ink-600">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-4 py-3 font-outfit text-sm text-ink-900">{u.logs_count}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openRecovery(u)}
                      className="inline-flex items-center gap-1.5 text-xs font-dmsans text-sage-600 hover:text-sage-700"
                      data-testid={`admin-recovery-${u.id}`}
                    >
                      <KeyRound size={12} /> Asistir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recovery drawer */}
      {activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/30" onClick={() => setActiveUser(null)} data-testid="admin-recovery-modal">
          <div className="tk-card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="tk-label">Asistencia de acceso</p>
                <h3 className="font-outfit text-xl text-ink-900 mt-1">{activeUser.name}</h3>
                <p className="font-dmsans text-sm text-ink-600">{activeUser.email}</p>
              </div>
              <button onClick={() => setActiveUser(null)} className="text-ink-600 hover:text-ink-900" data-testid="admin-recovery-close">
                <X size={18} />
              </button>
            </div>
            {recoveryLoading && <p className="text-sm text-ink-600">Cargando…</p>}
            {recovery && (
              <div className="space-y-3">
                {recovery.tokens?.length ? (
                  recovery.tokens.map((t) => (
                    <div key={t.id} className="border border-sand-200 rounded-2xl p-3 text-xs font-dmsans" data-testid={`admin-recovery-token-${t.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded-full ${t.used ? "bg-sand-100 text-ink-600" : "bg-sage-100 text-sage-700"}`}>
                          {t.used ? "Usado" : "Activo"}
                        </span>
                        <span className="text-ink-600">Vence: {new Date(t.expires_at).toLocaleString("es-AR")}</span>
                      </div>
                      <code className="block bg-sand-50 rounded-xl px-3 py-2 break-all text-ink-900">{t.token}</code>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-ink-600">Este usuario aún no solicitó recuperación.</p>
                )}
                <p className="text-xs text-ink-600 leading-relaxed">
                  Compartí el código activo con el usuario para que pueda definir una nueva contraseña en la pantalla de recuperación.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="tk-card !p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-600">
          <Icon size={18} />
        </div>
        <div>
          <p className="tk-label">{label}</p>
          <p className="font-outfit text-xl text-ink-900 mt-0.5">{value}</p>
        </div>
      </div>
    </div>
  );
}
